import React, { useEffect, useState, useMemo } from 'react';
import Layout from '../components/Layout';
import { inventory, invoices, proforma } from '../services/api';
import { FileText, Plus, Trash2, Download, Search, Package, Clock, PackagePlus, AlertTriangle, ChevronDown, ChevronUp, Copy, Check, ImagePlus, Pencil } from 'lucide-react';
import { useToast } from '../context/ToastContext';

const DEFAULT_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
const COLORS = ['Black', 'White', 'Grey', 'Navy', 'Red', 'Blue', 'Green', 'Yellow', 'Pink', 'Brown', 'Beige', 'Olive', 'Maroon'];

interface LineItem {
  inventory_id: number | null;
  article_type: string;
  color: string;
  size: string;
  quantity: number;
  rate: number;
  current_stock: number;
}

interface SizeRow {
  size: string;
  inventory_id: number;
  current_stock: number;
  cost_price: number;
  qty: number;
  rate: number;
  enabled: boolean;
}

const Proforma: React.FC = () => {
  const { toast, confirm } = useToast();
  const [dealers, setDealers] = useState<any[]>([]);
  const [inventoryItems, setInventoryItems] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [selectedDealer, setSelectedDealer] = useState<number>(0);
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [notes, setNotes] = useState('');
  const [gstRate, setGstRate] = useState(5);
  const [generating, setGenerating] = useState(false);
  const [history, setHistory] = useState<any[]>([]);

  const [expandedProforma, setExpandedProforma] = useState<number | null>(null);
  const [trackingInputs, setTrackingInputs] = useState<Record<number, string>>({});
  const [savingTracking, setSavingTracking] = useState<Record<number, boolean>>({});
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const [trackingsByProforma, setTrackingsByProforma] = useState<Record<number, any[]>>({});
  const [addTrackingInput, setAddTrackingInput] = useState<Record<number, { awb: string; note: string }>>({});
  const [editingTracking, setEditingTracking] = useState<{ proformaId: number; trackingId: number; awb: string; note: string } | null>(null);
  const [savingTracking2, setSavingTracking2] = useState(false);

  const [fabricImages, setFabricImages] = useState<string[]>([]);
  // Receive stock modal state
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [receiveProforma, setReceiveProforma] = useState<any>(null);
  const [receiveItems, setReceiveItems] = useState<any[]>([]);
  const [receiveNotes, setReceiveNotes] = useState('');
  const [creatingReceive, setCreatingReceive] = useState(false);

  // Bulk add modal state
  const [showBulkAdd, setShowBulkAdd] = useState(false);
  const [addMode, setAddMode] = useState<'inventory' | 'custom'>('inventory');
  const [bulkCategory, setBulkCategory] = useState('');
  const [bulkColor, setBulkColor] = useState('');
  const [bulkSizeRows, setBulkSizeRows] = useState<SizeRow[]>([]);
  const [bulkGlobalRate, setBulkGlobalRate] = useState('');
  // Custom item form
  const [customArticle, setCustomArticle] = useState('');
  const [customColor, setCustomColor] = useState('');
  const [customGlobalRate, setCustomGlobalRate] = useState('');
  const [customSizeRows, setCustomSizeRows] = useState<{ size: string; qty: number; rate: number; enabled: boolean }[]>(
    DEFAULT_SIZES.map(s => ({ size: s, qty: 0, rate: 0, enabled: false }))
  );

  const [showArchived, setShowArchived] = useState(false);
  const [archivedHistory, setArchivedHistory] = useState<any[]>([]);

  const loadHistory = () => {
    proforma.list(false).then(r => setHistory(r.data || [])).catch(() => {});
  };
  const loadArchived = () => {
    proforma.list(true).then(r => setArchivedHistory(r.data || [])).catch(() => {});
  };

  useEffect(() => {
    invoices.getDealers().then(r => setDealers(r.data)).catch(() => {});
    inventory.getAll().then(r => setInventoryItems(r.data)).catch(() => {});
    inventory.getCategories().then(r => setCategories(r.data)).catch(() => {});
    loadHistory();
  }, []);

  // Get unique article types from inventory
  const articleTypes = useMemo(() => {
    const set = new Set(inventoryItems.map(i => i.article_type));
    return Array.from(set).sort();
  }, [inventoryItems]);

  // Get available colors for selected article type
  const availableColors = useMemo(() => {
    if (!bulkCategory) return [];
    const set = new Set(inventoryItems.filter(i => i.article_type === bulkCategory).map(i => i.color));
    return Array.from(set).sort();
  }, [inventoryItems, bulkCategory]);

  // Build size grid when category+color selected
  useEffect(() => {
    if (!bulkCategory || !bulkColor) { setBulkSizeRows([]); return; }
    const matching = inventoryItems.filter(i => i.article_type === bulkCategory && i.color === bulkColor);
    const SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', '3XL', '28', '30', '32', '34', '36', '38', '40', 'FREE'];
    matching.sort((a, b) => {
      const ai = SIZE_ORDER.indexOf(a.size.toUpperCase());
      const bi = SIZE_ORDER.indexOf(b.size.toUpperCase());
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
    setBulkSizeRows(matching.map(item => ({
      size: item.size,
      inventory_id: item.id,
      current_stock: item.quantity,
      cost_price: item.cost_price,
      qty: 0,
      rate: item.cost_price || 0,
      enabled: false,
    })));
  }, [bulkCategory, bulkColor, inventoryItems]);

  const applyGlobalRate = () => {
    const rate = parseFloat(bulkGlobalRate);
    if (isNaN(rate)) return;
    setBulkSizeRows(prev => prev.map(r => r.enabled ? { ...r, rate } : r));
  };

  const toggleBulkSize = (index: number) => {
    setBulkSizeRows(prev => {
      const updated = [...prev];
      updated[index].enabled = !updated[index].enabled;
      if (!updated[index].enabled) { updated[index].qty = 0; }
      return updated;
    });
  };

  const updateBulkRow = (index: number, field: 'qty' | 'rate', value: number) => {
    setBulkSizeRows(prev => {
      const updated = [...prev];
      updated[index][field] = value;
      if (!updated[index].enabled) updated[index].enabled = true;
      return updated;
    });
  };

  const handleBulkConfirm = () => {
    const active = bulkSizeRows.filter(r => r.enabled && r.qty > 0);
    if (active.length === 0) return;
    const newItems: LineItem[] = active
      .filter(r => !lineItems.some(li => li.inventory_id === r.inventory_id))
      .map(r => ({
        inventory_id: r.inventory_id,
        article_type: bulkCategory,
        color: bulkColor,
        size: r.size,
        quantity: r.qty,
        rate: r.rate,
        current_stock: r.current_stock,
      }));
    setLineItems(prev => [...prev, ...newItems]);
    setShowBulkAdd(false);
    resetBulkForm();
  };

  const resetBulkForm = () => {
    setBulkCategory(''); setBulkColor(''); setBulkSizeRows([]); setBulkGlobalRate('');
    setCustomArticle(''); setCustomColor(''); setCustomGlobalRate('');
    setCustomSizeRows(DEFAULT_SIZES.map(s => ({ size: s, qty: 0, rate: 0, enabled: false })));
  };

  const toggleCustomSize = (index: number) => {
    setCustomSizeRows(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], enabled: !updated[index].enabled };
      if (!updated[index].enabled) updated[index] = { ...updated[index], qty: 0 };
      return updated;
    });
  };

  const updateCustomRow = (index: number, field: 'qty' | 'rate', value: number) => {
    setCustomSizeRows(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value, enabled: true };
      return updated;
    });
  };

  const applyCustomGlobalRate = () => {
    const rate = parseFloat(customGlobalRate);
    if (isNaN(rate)) return;
    setCustomSizeRows(prev => prev.map(r => r.enabled ? { ...r, rate } : r));
  };

  const handleAddCustomItem = () => {
    const active = customSizeRows.filter(r => r.enabled && r.qty > 0);
    if (!customArticle.trim() || active.length === 0) return;
    const newItems: LineItem[] = active.map(r => ({
      inventory_id: null,
      article_type: customArticle.trim(),
      color: customColor.trim() || '—',
      size: r.size,
      quantity: r.qty,
      rate: r.rate,
      current_stock: 0,
    }));
    setLineItems(prev => [...prev, ...newItems]);
    setCustomSizeRows(DEFAULT_SIZES.map(s => ({ size: s, qty: 0, rate: 0, enabled: false })));
    setCustomGlobalRate('');
  };

  const updateLine = (index: number, field: string, value: number) => {
    const updated = [...lineItems];
    (updated[index] as any)[field] = value;
    setLineItems(updated);
  };

  const removeLine = (index: number) => setLineItems(lineItems.filter((_, i) => i !== index));

  const subtotal = lineItems.reduce((sum, li) => sum + li.quantity * li.rate, 0);
  const gstAmount = Math.round(subtotal * (gstRate / 100) * 100) / 100;
  const grandTotal = subtotal + gstAmount;

  const bulkActive = bulkSizeRows.filter(r => r.enabled && r.qty > 0);
  const bulkTotalQty = bulkActive.reduce((s, r) => s + r.qty, 0);
  const bulkTotalAmount = bulkActive.reduce((s, r) => s + r.qty * r.rate, 0);

  const handleGenerate = async () => {
    if (!selectedDealer || lineItems.length === 0) return;
    setGenerating(true);
    try {
      const res = await proforma.generate({
        dealer_id: selectedDealer,
        items: lineItems.map(li => ({ inventory_id: li.inventory_id, article_type: li.article_type, color: li.color, size: li.size, quantity: li.quantity, rate: li.rate })),
        notes: notes || undefined, gst_rate: gstRate,
        fabric_images: fabricImages.length > 0 ? fabricImages : undefined,
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url;
      const dealer = dealers.find(d => d.id === selectedDealer);
      a.download = `proforma_${(dealer?.name || 'invoice').replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      a.click();
      toast('Proforma invoice generated successfully', 'success');
      loadHistory(); setLineItems([]); setNotes(''); setSelectedDealer(0); setFabricImages([]);
    } catch (e: any) {
      toast(e?.response?.data?.detail || 'Failed to generate PDF', 'error');
    }
    setGenerating(false);
  };

  const openReceiveModal = (p: any) => {
    let items: any[] = [];
    if (p.items_json) {
      try { items = JSON.parse(p.items_json); } catch { items = []; }
    }
    setReceiveProforma(p);
    setReceiveItems(items.map((item: any) => ({ ...item })));
    setReceiveNotes('');
    setShowReceiveModal(true);
  };

  const handleCreateReceive = async () => {
    if (!receiveProforma) return;
    setCreatingReceive(true);
    try {
      await proforma.createReceive({ proforma_id: receiveProforma.id, items: receiveItems, notes: receiveNotes || undefined });
      setShowReceiveModal(false);
      toast('Receive invoice created — go to Purchase Receives to add to stock', 'success');
    } catch (e: any) {
      toast(e?.response?.data?.detail || 'Failed to create receive invoice', 'error');
    }
    setCreatingReceive(false);
  };

  const handleDownload = async (id: number, num: string) => {
    try {
      const res = await proforma.download(id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `${num}.pdf`; a.click();
    } catch { toast('Download failed', 'error'); }
  };
  // Parse items_json and group by article_type+color → { sizes: [{size, qty}] }
  const groupProformaItems = (items_json: string | null) => {
    if (!items_json) return [];
    try {
      const items: any[] = JSON.parse(items_json);
      const groups: Record<string, { article_type: string; color: string; sizes: { size: string; qty: number }[] }> = {};
      items.forEach(item => {
        const key = `${item.article_type}||${item.color}`;
        if (!groups[key]) groups[key] = { article_type: item.article_type, color: item.color, sizes: [] };
        groups[key].sizes.push({ size: item.size, qty: item.quantity });
      });
      return Object.values(groups);
    } catch { return []; }
  };

  const handleSaveTracking = async (id: number) => {
    const awb = trackingInputs[id]?.trim();
    if (!awb) return;
    setSavingTracking(prev => ({ ...prev, [id]: true }));
    try {
      await proforma.saveTracking(id, awb);
      setHistory(prev => prev.map(p => p.id === id ? { ...p, tracking_number: awb } : p));
      setTrackingInputs(prev => { const n = { ...prev }; delete n[id]; return n; });
      toast('Tracking number saved', 'success');
    } catch { toast('Failed to save tracking number', 'error'); }
    finally { setSavingTracking(prev => ({ ...prev, [id]: false })); }
  };

  const loadTrackings = async (proformaId: number) => {
    try {
      const res = await proforma.getTrackings(proformaId);
      setTrackingsByProforma(prev => ({ ...prev, [proformaId]: res.data }));
    } catch { }
  };

  const handleExpandRow = (id: number) => {
    const next = expandedProforma === id ? null : id;
    setExpandedProforma(next);
    if (next !== null && !trackingsByProforma[next]) {
      loadTrackings(next);
    }
  };

  const handleAddTracking = async (proformaId: number) => {
    const input = addTrackingInput[proformaId];
    if (!input?.awb?.trim()) return;
    setSavingTracking2(true);
    try {
      await proforma.addTracking(proformaId, input.awb.trim(), input.note?.trim() || undefined);
      setAddTrackingInput(prev => ({ ...prev, [proformaId]: { awb: '', note: '' } }));
      await loadTrackings(proformaId);
      toast('AWB added', 'success');
    } catch { toast('Failed to add AWB', 'error'); }
    finally { setSavingTracking2(false); }
  };

  const handleUpdateTracking = async () => {
    if (!editingTracking) return;
    setSavingTracking2(true);
    try {
      await proforma.updateTracking(editingTracking.proformaId, editingTracking.trackingId, editingTracking.awb.trim(), editingTracking.note?.trim() || undefined);
      await loadTrackings(editingTracking.proformaId);
      setEditingTracking(null);
      toast('AWB updated', 'success');
    } catch { toast('Failed to update AWB', 'error'); }
    finally { setSavingTracking2(false); }
  };

  const handleDeleteTracking = async (proformaId: number, trackingId: number) => {
    try {
      await proforma.deleteTracking(proformaId, trackingId);
      await loadTrackings(proformaId);
      toast('AWB removed', 'success');
    } catch { toast('Failed to remove AWB', 'error'); }
  };

  const handleCopyTracking = (id: number, num: string) => {
    navigator.clipboard.writeText(num);
    setCopiedId(id);
    setTimeout(() => setCopiedId(c => c === id ? null : c), 2000);
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm('Delete this proforma? This cannot be undone.');
    if (!ok) return;
    try { await proforma.delete(id); toast('Proforma deleted', 'success'); loadHistory(); }
    catch { toast('Failed to delete proforma', 'error'); }
  };

  const inputStyle: React.CSSProperties = { width: '100%', padding: '11px 14px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' };
  const labelStyle: React.CSSProperties = { fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 6, fontWeight: 600 };

  return (
    <Layout title="Proforma Invoice" subtitle="Create purchase orders for dealers">
      <div>
        {/* Dealer & Settings */}
        <div className="animate-in" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 24, marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 16 }}>
            <div>
              <label style={labelStyle}>Select Dealer</label>
              <select value={selectedDealer} onChange={e => setSelectedDealer(Number(e.target.value))} style={inputStyle}>
                <option value={0}>Choose a dealer...</option>
                {dealers.map(d => <option key={d.id} value={d.id}>{d.name}{d.gst_number ? ` (${d.gst_number})` : ''}</option>)}
              </select>
              {dealers.length === 0 && <div style={{ fontSize: 12, color: 'var(--accent-amber)', marginTop: 6 }}>No dealers found. Add dealers in Invoices → Manage Dealers first.</div>}
            </div>
            <div>
              <label style={labelStyle}>GST Rate (%)</label>
              <select value={gstRate} onChange={e => setGstRate(Number(e.target.value))} style={inputStyle}>
                <option value={0}>No GST</option>
                <option value={5}>5%</option>
                <option value={12}>12%</option>
                <option value={18}>18%</option>
                <option value={28}>28%</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Fabric Images (optional, up to 4)</label>
              {fabricImages.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                  {fabricImages.map((img, idx) => (
                    <div key={idx} style={{ position: 'relative', display: 'inline-flex' }}>
                      <img src={img} alt={`Fabric ${idx + 1}`} style={{ height: 48, width: 48, objectFit: 'cover', borderRadius: 4, border: '1px solid var(--border)' }} />
                      <button
                        onClick={() => setFabricImages(prev => prev.filter((_, i) => i !== idx))}
                        title="Remove"
                        style={{ position: 'absolute', top: -6, right: -6, background: 'var(--accent-red-soft)', border: '1px solid rgba(239,90,111,0.3)', borderRadius: '50%', width: 18, height: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                      >
                        <Trash2 size={10} color="var(--accent-red)" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {fabricImages.length < 4 && (
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 12px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 12, fontWeight: 500 }}>
                  <ImagePlus size={14} />
                  <span>{fabricImages.length === 0 ? 'Upload image' : 'Add another'}</span>
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = ev => {
                      const result = ev.target?.result as string;
                      setFabricImages(prev => prev.length < 4 ? [...prev, result] : prev);
                    };
                    reader.readAsDataURL(file);
                    e.target.value = '';
                  }} />
                </label>
              )}
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div className="animate-in-1" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', marginBottom: 16 }}>
          <div style={{ padding: '16px 22px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Package size={15} color="var(--accent-blue)" />
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Order Items</span>
              {lineItems.length > 0 && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: 'var(--accent-blue-soft)', color: 'var(--accent-blue)', fontWeight: 600 }}>{lineItems.length} items</span>}
            </div>
            <button onClick={() => { resetBulkForm(); setAddMode('inventory'); setShowBulkAdd(true); }} style={{
              padding: '7px 14px', background: 'linear-gradient(135deg, var(--accent-blue), var(--accent-purple))',
              border: 'none', borderRadius: 'var(--radius-sm)', color: '#fff', fontSize: 12, fontWeight: 600,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
            }}><Plus size={13} /> Add Items</button>
          </div>

          {lineItems.length > 0 ? (
            <div className="table-responsive">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Article', 'Color', 'Size', 'Stock', 'Qty', 'Rate (₹)', 'Amount (₹)', ''].map(h => (
                      <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 10.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((li, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '10px 16px', fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{li.article_type}</td>
                      <td style={{ padding: '10px 16px', fontSize: 13, color: 'var(--text-secondary)' }}>{li.color}</td>
                      <td style={{ padding: '10px 16px' }}><span style={{ background: 'var(--accent-blue-soft)', color: 'var(--accent-blue)', padding: '2px 8px', borderRadius: 6, fontSize: 11.5, fontWeight: 600 }}>{li.size}</span></td>
                      <td style={{ padding: '10px 16px', fontSize: 12, color: li.inventory_id !== null && li.quantity > li.current_stock ? 'var(--accent-amber)' : 'var(--text-muted)' }}>
                        {li.inventory_id !== null ? li.current_stock : <span style={{ fontSize: 11, color: 'var(--accent-purple)', fontWeight: 600 }}>custom</span>}
                        {li.inventory_id !== null && li.quantity > li.current_stock && <span title="Order qty exceeds current stock" style={{ marginLeft: 4 }}><AlertTriangle size={11} style={{ verticalAlign: 'middle' }} /></span>}
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        <input type="number" min={1} value={li.quantity} onChange={e => updateLine(i, 'quantity', Math.max(1, Number(e.target.value)))}
                          style={{ width: 65, padding: '6px 8px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 13, outline: 'none', textAlign: 'center' }} />
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        <input type="number" min={0} step={0.01} value={li.rate} onChange={e => updateLine(i, 'rate', Math.max(0, Number(e.target.value)))}
                          style={{ width: 80, padding: '6px 8px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--accent-amber)', fontSize: 13, outline: 'none', textAlign: 'right' }} />
                      </td>
                      <td style={{ padding: '10px 16px', fontSize: 13, color: 'var(--accent-green)', fontWeight: 600 }}>₹{(li.quantity * li.rate).toLocaleString('en-IN')}</td>
                      <td style={{ padding: '10px 16px' }}>
                        <button onClick={() => removeLine(i)} style={{ background: 'var(--accent-red-soft)', border: '1px solid rgba(239,90,111,0.12)', borderRadius: 6, padding: '4px 7px', cursor: 'pointer', display: 'flex' }}><Trash2 size={13} color="var(--accent-red)" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)', fontSize: 13 }}>Click "Add Items" to select from your inventory.</div>
          )}
        </div>

        {/* Notes + Totals side by side */}
        {lineItems.length > 0 && (
          <div className="animate-in-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 22 }}>
              <label style={labelStyle}>Notes / Terms</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Payment terms, delivery instructions, etc."
                style={{ ...inputStyle, height: 100, resize: 'vertical', fontFamily: 'inherit' }} />
            </div>
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 22 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Subtotal</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>₹{subtotal.toLocaleString('en-IN')}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>GST ({gstRate}%)</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>₹{gstAmount.toLocaleString('en-IN')}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 0 4px' }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Grand Total</span>
                <span style={{ fontSize: 22, fontWeight: 800, color: 'var(--accent-green)', letterSpacing: -0.5 }}>₹{grandTotal.toLocaleString('en-IN')}</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{lineItems.length} items · {lineItems.reduce((s, li) => s + li.quantity, 0)} total qty</div>
            </div>
          </div>
        )}

        {/* Generate */}
        {lineItems.length > 0 && (
          <div className="animate-in-3" style={{ marginBottom: 24 }}>
            <button onClick={handleGenerate} disabled={generating || !selectedDealer} style={{
              padding: '14px 28px', background: (selectedDealer && lineItems.length > 0) ? 'linear-gradient(135deg, var(--accent-green), #059669)' : 'var(--bg-elevated)',
              border: 'none', borderRadius: 'var(--radius-md)', color: (selectedDealer && lineItems.length > 0) ? '#fff' : 'var(--text-muted)',
              fontSize: 15, fontWeight: 700, cursor: (selectedDealer && lineItems.length > 0) ? 'pointer' : 'default', display: 'flex', alignItems: 'center', gap: 10,
            }}><Download size={18} /> {generating ? 'Generating PDF...' : 'Generate Proforma Invoice'}</button>
          </div>
        )}

        {/* History */}
        <div className="animate-in-3" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <div style={{ padding: '16px 22px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Clock size={15} color="var(--accent-purple)" />
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Generated Proformas</span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', background: 'var(--bg-elevated)', padding: '2px 8px', borderRadius: 10 }}>{history.length}</span>
            <div style={{ marginLeft: 'auto' }}>
              <button onClick={() => { setShowArchived(!showArchived); if (!showArchived) loadArchived(); }} style={{ padding: '6px 14px', borderRadius: 'var(--radius-sm)', fontSize: 12, fontWeight: 600, cursor: 'pointer', background: showArchived ? 'var(--accent-amber-soft)' : 'var(--bg-elevated)', border: `1px solid ${showArchived ? 'rgba(245,166,35,0.2)' : 'var(--border)'}`, color: showArchived ? 'var(--accent-amber)' : 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                📦 Archived {archivedHistory.length > 0 && `(${archivedHistory.length})`}
              </button>
            </div>
          </div>
          {history.length > 0 ? (
            <div className="table-responsive">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Invoice #', 'Dealer', 'Products', 'Qty', 'Total', 'Date', 'Lead Time', 'Tracking', ''].map(h => (
                      <th key={h} style={{ padding: '10px 16px', textAlign: h === 'Invoice #' || h === 'Dealer' || h === 'Products' || h === 'Tracking' ? 'left' : 'right', fontSize: 10.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {history.map((p: any) => {
                    const groups = groupProformaItems(p.items_json);
                    const isExpanded = expandedProforma === p.id;
                    return (
                      <React.Fragment key={p.id}>
                        <tr style={{ borderBottom: isExpanded ? 'none' : '1px solid var(--border)' }}>
                          <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--accent-blue)', fontWeight: 600 }}>{p.invoice_number}</td>
                          <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text-primary)' }}>{p.dealer_name}</td>
                          <td style={{ padding: '12px 16px' }}>
                            {groups.length > 0 ? (
                              <div>
                                {/* Always show first product inline */}
                                <div style={{ fontSize: 12.5, color: 'var(--text-primary)', fontWeight: 500 }}>
                                  {groups[0].article_type}
                                  <span style={{ color: 'var(--text-muted)', marginLeft: 4 }}>({groups[0].color})</span>
                                  <span style={{ color: 'var(--text-muted)', fontSize: 11.5, marginLeft: 6 }}>
                                    {groups[0].sizes.map(s => `${s.size}-${s.qty}`).join(', ')}
                                  </span>
                                </div>
                                <button
                                  onClick={() => handleExpandRow(p.id)}
                                  style={{ marginTop: 4, background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, color: 'var(--accent-blue)' }}
                                >
                                  {isExpanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                                  {isExpanded ? 'hide' : groups.length > 1 ? `+${groups.length - 1} more` : '+ trackings'}
                                </button>
                              </div>
                            ) : (
                              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{p.items_count} items</span>
                            )}
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 13, color: 'var(--text-secondary)' }}>{p.total_qty}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 13, fontWeight: 700, color: 'var(--accent-green)' }}>₹{(p.grand_total || 0).toLocaleString('en-IN')}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 12, color: 'var(--text-muted)' }}>{new Date(p.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                            {p.days_to_full_receive != null ? (
                              <span style={{ fontSize: 12, fontWeight: 600, color: p.days_to_full_receive <= 7 ? 'var(--accent-green)' : p.days_to_full_receive <= 14 ? 'var(--accent-amber)' : 'var(--accent-red)' }}>
                                {p.days_to_full_receive}d
                              </span>
                            ) : p.days_pending != null ? (
                              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{p.days_pending}d pending</span>
                            ) : '—'}
                          </td>
                          <td style={{ padding: '12px 16px', minWidth: 170 }}>
                            {p.tracking_number && trackingInputs[p.id] === undefined ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'monospace' }}>{p.tracking_number}</span>
                                <button onClick={() => handleCopyTracking(p.id, p.tracking_number)} title="Copy tracking number" style={{ background: 'none', border: 'none', padding: '2px 4px', cursor: 'pointer', display: 'flex', alignItems: 'center', color: copiedId === p.id ? 'var(--accent-green)' : 'var(--text-muted)', borderRadius: 4 }}>
                                  {copiedId === p.id ? <Check size={13} /> : <Copy size={13} />}
                                </button>
                                <button onClick={() => setTrackingInputs(prev => ({ ...prev, [p.id]: p.tracking_number }))} title="Edit tracking number" style={{ background: 'none', border: 'none', padding: '2px 4px', cursor: 'pointer', display: 'flex', alignItems: 'center', color: 'var(--text-muted)', borderRadius: 4 }}>
                                  <Pencil size={12} />
                                </button>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                                <input
                                  value={trackingInputs[p.id] ?? ''}
                                  onChange={e => setTrackingInputs(prev => ({ ...prev, [p.id]: e.target.value }))}
                                  placeholder="Add docket..."
                                  onKeyDown={e => { if (e.key === 'Enter') handleSaveTracking(p.id); if (e.key === 'Escape' && p.tracking_number) setTrackingInputs(prev => { const n = { ...prev }; delete n[p.id]; return n; }); }}
                                  style={{ fontSize: 12, padding: '4px 8px', borderRadius: 5, border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', width: 110, outline: 'none' }}
                                  autoFocus
                                />
                                <button onClick={() => handleSaveTracking(p.id)} disabled={savingTracking[p.id] || !trackingInputs[p.id]?.trim()} style={{ background: 'var(--accent-blue-soft)', border: '1px solid rgba(79,125,249,0.15)', borderRadius: 5, padding: '4px 8px', cursor: 'pointer', fontSize: 11, color: 'var(--accent-blue)', fontWeight: 600, opacity: savingTracking[p.id] || !trackingInputs[p.id]?.trim() ? 0.4 : 1 }}>
                                  {savingTracking[p.id] ? '…' : 'Save'}
                                </button>
                                {p.tracking_number && (
                                  <button onClick={() => setTrackingInputs(prev => { const n = { ...prev }; delete n[p.id]; return n; })} title="Cancel" style={{ background: 'none', border: 'none', padding: '2px 4px', cursor: 'pointer', fontSize: 13, color: 'var(--text-muted)' }}>✕</button>
                                )}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                            <div style={{ display: 'flex', gap: 5, justifyContent: 'flex-end' }}>
                              <button onClick={() => openReceiveModal(p)} style={{ background: 'var(--accent-amber-soft, rgba(245,158,11,0.1))', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, color: 'var(--accent-amber)', fontSize: 11, fontWeight: 600 }}><PackagePlus size={12} /> Receive</button>
                              <button onClick={() => handleDownload(p.id, p.invoice_number)} style={{ background: 'var(--accent-blue-soft)', border: '1px solid rgba(79,125,249,0.15)', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, color: 'var(--accent-blue)', fontSize: 11, fontWeight: 600 }}><Download size={12} /> PDF</button>
                              {(trackingsByProforma[p.id] || []).length > 1 && (
                                <button onClick={async () => { if (await confirm('Archive this proforma? It will be hidden from the main list.')) { await proforma.archive(p.id); loadHistory(); loadArchived(); toast('Proforma archived', 'success'); } }} style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.15)', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, color: 'var(--accent-amber)', fontSize: 11, fontWeight: 600 }}>📦 Archive</button>
                              )}
                              <button onClick={() => handleDelete(p.id)} style={{ background: 'var(--accent-red-soft)', border: '1px solid rgba(239,90,111,0.12)', borderRadius: 6, padding: '5px 7px', cursor: 'pointer', display: 'flex' }}><Trash2 size={12} color="var(--accent-red)" /></button>
                            </div>
                          </td>
                        </tr>
                        {/* Expanded tracking + product breakdown */}
                        {isExpanded && (
                          <tr style={{ borderBottom: '1px solid var(--border)' }}>
                            <td colSpan={8} style={{ padding: '0 16px 14px 32px', background: 'var(--bg-elevated)' }}>
                              <div style={{ paddingTop: 10 }}>
                                {/* Multi-Tracking Panel */}
                                <div style={{ marginBottom: groups.length > 1 ? 16 : 0 }}>
                                  <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600, marginBottom: 8 }}>
                                    AWB / Docket Numbers
                                  </div>
                                  {(trackingsByProforma[p.id] || []).map((t: any) => (
                                    <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, padding: '7px 10px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 6 }}>
                                      {editingTracking && editingTracking.trackingId === t.id ? (
                                        <>
                                          <input
                                            value={editingTracking.awb}
                                            onChange={e => setEditingTracking({ ...editingTracking, awb: e.target.value })}
                                            onKeyDown={e => { if (e.key === 'Enter') handleUpdateTracking(); if (e.key === 'Escape') setEditingTracking(null); }}
                                            placeholder="AWB number"
                                            style={{ fontSize: 12, padding: '4px 8px', borderRadius: 5, border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', width: 130, outline: 'none', fontFamily: 'monospace' }}
                                          />
                                          <input
                                            value={editingTracking.note}
                                            onChange={e => setEditingTracking({ ...editingTracking, note: e.target.value })}
                                            onKeyDown={e => { if (e.key === 'Enter') handleUpdateTracking(); if (e.key === 'Escape') setEditingTracking(null); }}
                                            placeholder="Note (e.g. S/M/L)"
                                            style={{ fontSize: 12, padding: '4px 8px', borderRadius: 5, border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', flex: 1, outline: 'none' }}
                                          />
                                          <button onClick={handleUpdateTracking} disabled={savingTracking2 || !editingTracking.awb.trim()} style={{ background: 'var(--accent-green-soft)', border: '1px solid rgba(45,212,160,0.2)', borderRadius: 5, padding: '3px 8px', cursor: 'pointer', fontSize: 12, color: 'var(--accent-green)', fontWeight: 700, opacity: savingTracking2 || !editingTracking.awb.trim() ? 0.4 : 1 }}>✓</button>
                                          <button onClick={() => setEditingTracking(null)} style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 5, padding: '3px 8px', cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)' }}>✕</button>
                                        </>
                                      ) : (
                                        <>
                                          <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--accent-purple)', fontFamily: 'monospace', minWidth: 130 }}>{t.awb}</span>
                                          <span style={{ fontSize: 12, color: 'var(--text-muted)', flex: 1 }}>{t.note || '—'}</span>
                                          <button onClick={() => setEditingTracking({ proformaId: p.id, trackingId: t.id, awb: t.awb, note: t.note || '' })} title="Edit" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', display: 'flex', color: 'var(--text-muted)' }}>
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                          </button>
                                          <button onClick={() => handleDeleteTracking(p.id, t.id)} title="Delete" style={{ background: 'var(--accent-red-soft)', border: '1px solid rgba(239,90,111,0.12)', borderRadius: 5, padding: '3px 6px', cursor: 'pointer', display: 'flex' }}>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--accent-red)" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  ))}
                                  {/* Add new AWB row */}
                                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4 }}>
                                    <input
                                      value={addTrackingInput[p.id]?.awb || ''}
                                      onChange={e => setAddTrackingInput(prev => ({ ...prev, [p.id]: { ...prev[p.id], awb: e.target.value, note: prev[p.id]?.note || '' } }))}
                                      onKeyDown={e => { if (e.key === 'Enter') handleAddTracking(p.id); }}
                                      placeholder="Add AWB / docket..."
                                      style={{ fontSize: 12, padding: '5px 9px', borderRadius: 5, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', width: 140, outline: 'none', fontFamily: 'monospace' }}
                                    />
                                    <input
                                      value={addTrackingInput[p.id]?.note || ''}
                                      onChange={e => setAddTrackingInput(prev => ({ ...prev, [p.id]: { ...prev[p.id], note: e.target.value, awb: prev[p.id]?.awb || '' } }))}
                                      onKeyDown={e => { if (e.key === 'Enter') handleAddTracking(p.id); }}
                                      placeholder="Note (e.g. S/M/L)"
                                      style={{ fontSize: 12, padding: '5px 9px', borderRadius: 5, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', flex: 1, outline: 'none' }}
                                    />
                                    <button
                                      onClick={() => handleAddTracking(p.id)}
                                      disabled={savingTracking2 || !addTrackingInput[p.id]?.awb?.trim()}
                                      style={{ background: 'var(--accent-blue-soft)', border: '1px solid rgba(79,125,249,0.15)', borderRadius: 5, padding: '5px 10px', cursor: 'pointer', fontSize: 11, color: 'var(--accent-blue)', fontWeight: 600, opacity: savingTracking2 || !addTrackingInput[p.id]?.awb?.trim() ? 0.4 : 1 }}
                                    >
                                      {savingTracking2 ? '…' : '+ Add'}
                                    </button>
                                  </div>
                                </div>

                                {/* Product breakdown (existing) */}
                                {groups.length > 1 && (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                    {groups.map((g, gi) => (
                                      <div key={gi} style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                                        <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)', minWidth: 160 }}>{g.article_type}</span>
                                        <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{g.color}</span>
                                        <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>·</span>
                                        <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
                                          {g.sizes.map((s: any) => `${s.size}-${s.qty}`).join(', ')}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : <div style={{ textAlign: 'center', padding: '30px 20px', color: 'var(--text-muted)', fontSize: 13 }}>No proformas generated yet.</div>}

          {/* Archived section */}
          {showArchived && (
            <div style={{ borderTop: '1px solid var(--border)' }}>
              <div style={{ padding: '12px 22px', background: 'var(--accent-amber-soft)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent-amber)', textTransform: 'uppercase', letterSpacing: 0.6 }}>📦 Archived — {archivedHistory.length} proforma{archivedHistory.length !== 1 ? 's' : ''}</span>
              </div>
              {archivedHistory.length === 0
                ? <div style={{ textAlign: 'center', padding: '24px 20px', color: 'var(--text-muted)', fontSize: 13 }}>No archived proformas.</div>
                : <table style={{ width: '100%', borderCollapse: 'collapse', opacity: 0.7 }}>
                    <tbody>
                      {archivedHistory.map((p: any) => (
                        <tr key={p.id} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '10px 18px', fontSize: 13, color: 'var(--text-muted)', fontWeight: 600 }}>{p.invoice_number}</td>
                          <td style={{ padding: '10px 18px', fontSize: 13, color: 'var(--text-muted)' }}>{p.dealer_name}</td>
                          <td style={{ padding: '10px 18px', fontSize: 13, color: 'var(--text-muted)' }}>{p.total_qty} pcs</td>
                          <td style={{ padding: '10px 18px', fontSize: 13, color: 'var(--text-muted)' }}>₹{(p.grand_total || 0).toLocaleString('en-IN')}</td>
                          <td style={{ padding: '10px 18px', fontSize: 12, color: 'var(--text-muted)' }}>{new Date(p.created_at).toLocaleDateString('en-IN')}</td>
                          <td style={{ padding: '10px 18px' }}>
                            <button onClick={async () => { const res = await proforma.download(p.id); const url = window.URL.createObjectURL(new Blob([res.data])); const a = document.createElement('a'); a.href = url; a.download = `${p.invoice_number}.pdf`; a.click(); }} style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 11, color: 'var(--text-muted)' }}>PDF</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
              }
            </div>
          )}
        </div>
      </div>

      {/* ═══ RECEIVE STOCK MODAL ═══ */}
      {showReceiveModal && receiveProforma && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
          onClick={e => { if (e.target === e.currentTarget) setShowReceiveModal(false); }}>
          <div className="animate-in" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', width: 620, maxWidth: '95vw', boxShadow: 'var(--shadow-elevated)', maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ padding: '24px 28px 16px', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 2, borderRadius: 'var(--radius-xl) var(--radius-xl) 0 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <PackagePlus size={18} color="var(--accent-amber)" /> Receive Stock
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                    From proforma <span style={{ color: 'var(--accent-blue)', fontWeight: 600 }}>{receiveProforma.invoice_number}</span> · {receiveProforma.dealer_name}
                  </div>
                </div>
                <button onClick={() => setShowReceiveModal(false)} style={{ background: 'var(--bg-elevated)', border: 'none', borderRadius: 8, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 16, color: 'var(--text-muted)' }}>×</button>
              </div>
            </div>
            <div style={{ padding: '20px 28px 28px' }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600, marginBottom: 10 }}>Adjust Received Quantities</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}>
                {receiveItems.map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{item.article_type} — {item.color}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Size: <span style={{ color: 'var(--accent-blue)', fontWeight: 600 }}>{item.size}</span> · Rate: ₹{item.rate}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Proforma: {item.quantity}</span>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>→</span>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Received:</span>
                      <input type="number" min={0} value={receiveItems[i].quantity}
                        onChange={e => {
                          const updated = [...receiveItems];
                          updated[i] = { ...updated[i], quantity: Math.max(0, Number(e.target.value)) };
                          setReceiveItems(updated);
                        }}
                        style={{ width: 70, padding: '6px 8px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 13, outline: 'none', textAlign: 'center' }} />
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>Notes (optional)</label>
                <textarea value={receiveNotes} onChange={e => setReceiveNotes(e.target.value)} placeholder="Any discrepancies, damaged goods, etc."
                  style={{ ...inputStyle, height: 72, resize: 'vertical', fontFamily: 'inherit' }} />
              </div>
              <div style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', padding: '10px 14px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Total received qty</span>
                <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)' }}>{receiveItems.reduce((s, r) => s + (r.quantity || 0), 0)}</span>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={handleCreateReceive} disabled={creatingReceive || receiveItems.every(r => !r.quantity)} style={{
                  flex: 1, padding: 12,
                  background: 'linear-gradient(135deg, var(--accent-amber, #f59e0b), #d97706)',
                  border: 'none', borderRadius: 'var(--radius-sm)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}>
                  {creatingReceive ? 'Creating...' : 'Create Purchase Receive Invoice'}
                </button>
                <button onClick={() => setShowReceiveModal(false)} style={{ padding: '12px 20px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* ═══ BULK ADD MODAL — Inventory style ═══ */}
      {showBulkAdd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
          onClick={e => { if (e.target === e.currentTarget) setShowBulkAdd(false); }}>
          <div className="animate-in" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: 0, width: 560, maxWidth: '95vw', boxShadow: 'var(--shadow-elevated)', maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ padding: '24px 28px 16px', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 2, borderRadius: 'var(--radius-xl) var(--radius-xl) 0 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>Add Items to Proforma</div>
                <button onClick={() => setShowBulkAdd(false)} style={{ background: 'var(--bg-elevated)', border: 'none', borderRadius: 8, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 16, color: 'var(--text-muted)' }}>×</button>
              </div>
            </div>

            <div style={{ padding: '20px 28px 28px' }}>
              {/* Mode Toggle */}
              <div style={{ display: 'flex', gap: 0, marginBottom: 20, background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', padding: 3 }}>
                {(['inventory', 'custom'] as const).map(mode => (
                  <button key={mode} onClick={() => setAddMode(mode)} style={{
                    flex: 1, padding: '8px 0', borderRadius: 'var(--radius-sm)', fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none',
                    background: addMode === mode ? 'var(--bg-card)' : 'transparent',
                    color: addMode === mode ? 'var(--text-primary)' : 'var(--text-muted)',
                    boxShadow: addMode === mode ? 'var(--shadow-sm, 0 1px 3px rgba(0,0,0,0.2))' : 'none',
                  }}>
                    {mode === 'inventory' ? 'From Inventory' : 'Custom Item'}
                  </button>
                ))}
              </div>

              {/* ── Custom Item Form ── */}
              {addMode === 'custom' && (() => {
                const customActive = customSizeRows.filter(r => r.enabled && r.qty > 0);
                const customTotalQty = customActive.reduce((s, r) => s + r.qty, 0);
                const customTotalAmt = customActive.reduce((s, r) => s + r.qty * r.rate, 0);
                const canAdd = customArticle.trim() && customActive.length > 0;
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* Article + Color */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div>
                        <label style={labelStyle}>Article Type *</label>
                        <input value={customArticle} onChange={e => setCustomArticle(e.target.value)}
                          placeholder="e.g. Oversized Tee" style={inputStyle} />
                      </div>
                      <div>
                        <label style={labelStyle}>Color</label>
                        <input value={customColor} onChange={e => setCustomColor(e.target.value)}
                          placeholder="e.g. Black" list="custom-colors-list" style={inputStyle} />
                        <datalist id="custom-colors-list">
                          {COLORS.map(c => <option key={c} value={c} />)}
                        </datalist>
                      </div>
                    </div>

                    {/* Size grid */}
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                        <label style={{ ...labelStyle, marginBottom: 0 }}>Sizes — Qty & Rate per piece</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <input type="number" value={customGlobalRate} onChange={e => setCustomGlobalRate(e.target.value)}
                            placeholder="₹ rate" style={{ width: 80, padding: '5px 8px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 12, outline: 'none' }} />
                          <button onClick={applyCustomGlobalRate} style={{ padding: '5px 10px', background: 'var(--accent-blue-soft)', border: 'none', borderRadius: 6, color: 'var(--accent-blue)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Apply to all</button>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {customSizeRows.map((row, i) => (
                          <div key={row.size} style={{
                            display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 'var(--radius-sm)',
                            background: row.enabled ? 'var(--bg-elevated)' : 'transparent',
                            border: `1px solid ${row.enabled ? 'var(--border-light)' : 'var(--border)'}`,
                          }}>
                            <button onClick={() => toggleCustomSize(i)} style={{
                              width: 50, padding: '5px 0', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                              background: row.enabled ? 'var(--accent-blue)' : 'var(--bg-input)',
                              border: 'none', color: row.enabled ? '#fff' : 'var(--text-muted)', textAlign: 'center',
                            }}>{row.size}</button>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <span style={{ fontSize: 10.5, color: 'var(--text-muted)', width: 25 }}>Qty</span>
                              <input type="number" min={0} value={row.qty || ''} onChange={e => updateCustomRow(i, 'qty', Number(e.target.value))}
                                placeholder="0"
                                style={{ width: 65, padding: '6px 8px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 13, outline: 'none', textAlign: 'center' }} />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <span style={{ fontSize: 10.5, color: 'var(--text-muted)', width: 15 }}>₹</span>
                              <input type="number" min={0} step={0.01} value={row.rate || ''} onChange={e => updateCustomRow(i, 'rate', Number(e.target.value))}
                                placeholder="0"
                                style={{ width: 80, padding: '6px 8px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--accent-amber)', fontSize: 13, outline: 'none', textAlign: 'right' }} />
                            </div>
                            <div style={{ flex: 1, textAlign: 'right', fontSize: 12, color: row.enabled && row.qty > 0 ? 'var(--accent-green)' : 'var(--text-muted)', fontWeight: 600 }}>
                              {row.qty > 0 && row.rate > 0 ? `₹${(row.qty * row.rate).toLocaleString('en-IN')}` : '—'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Totals */}
                    {customActive.length > 0 && (
                      <div style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', padding: '14px 16px', display: 'flex', justifyContent: 'space-between' }}>
                        <div><div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginBottom: 4 }}>Total Qty</div><div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)' }}>{customTotalQty}</div></div>
                        <div style={{ textAlign: 'right' }}><div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginBottom: 4 }}>Total Amount</div><div style={{ fontSize: 18, fontWeight: 800, color: 'var(--accent-amber)' }}>₹{customTotalAmt.toLocaleString('en-IN')}</div></div>
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 10 }}>
                      <button onClick={handleAddCustomItem} disabled={!canAdd} style={{
                        flex: 1, padding: 12,
                        background: canAdd ? 'linear-gradient(135deg, var(--accent-blue), var(--accent-purple))' : 'var(--bg-elevated)',
                        border: 'none', borderRadius: 'var(--radius-sm)',
                        color: canAdd ? '#fff' : 'var(--text-muted)', fontSize: 13, fontWeight: 600, cursor: canAdd ? 'pointer' : 'default',
                      }}>Add {customActive.length} Size{customActive.length !== 1 ? 's' : ''} to Order</button>
                      <button onClick={() => setShowBulkAdd(false)} style={{ padding: '12px 20px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>Done</button>
                    </div>
                  </div>
                );
              })()}

              {/* ── Inventory Mode ── */}
              {addMode === 'inventory' && <>
              {/* Category / Article Type */}
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Article Type</label>
                <select value={bulkCategory} onChange={e => { setBulkCategory(e.target.value); setBulkColor(''); }} style={inputStyle}>
                  <option value="">Select article...</option>
                  {articleTypes.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>

              {/* Color */}
              {bulkCategory && (
                <div style={{ marginBottom: 20 }}>
                  <label style={labelStyle}>Color</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {availableColors.map(c => (
                      <button key={c} onClick={() => setBulkColor(c)} style={{
                        padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                        background: bulkColor === c ? 'var(--accent-blue-soft)' : 'var(--bg-elevated)',
                        border: `1px solid ${bulkColor === c ? 'rgba(79,125,249,0.3)' : 'var(--border)'}`,
                        color: bulkColor === c ? 'var(--accent-blue)' : 'var(--text-secondary)',
                      }}>{c}</button>
                    ))}
                  </div>
                  {availableColors.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>No colors available for this article.</div>}
                </div>
              )}

              {/* Size Grid */}
              {bulkSizeRows.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <label style={{ ...labelStyle, marginBottom: 0 }}>Sizes — Qty & Rate per piece</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="number" value={bulkGlobalRate} onChange={e => setBulkGlobalRate(e.target.value)} placeholder="₹ rate" style={{ width: 80, padding: '5px 8px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 12, outline: 'none' }} />
                      <button onClick={applyGlobalRate} style={{ padding: '5px 10px', background: 'var(--accent-blue-soft)', border: 'none', borderRadius: 6, color: 'var(--accent-blue)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Apply to all</button>
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {bulkSizeRows.map((row, i) => {
                      const alreadyInList = lineItems.some(li => li.inventory_id === row.inventory_id);
                      return (
                        <div key={row.size} style={{
                          display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 'var(--radius-sm)',
                          background: row.enabled ? 'var(--bg-elevated)' : 'transparent',
                          border: `1px solid ${row.enabled ? 'var(--border-light)' : 'var(--border)'}`,
                          opacity: alreadyInList ? 0.4 : 1,
                        }}>
                          <button onClick={() => !alreadyInList && toggleBulkSize(i)} style={{
                            width: 50, padding: '5px 0', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: alreadyInList ? 'default' : 'pointer',
                            background: row.enabled ? 'var(--accent-blue)' : 'var(--bg-input)',
                            border: 'none', color: row.enabled ? '#fff' : 'var(--text-muted)', textAlign: 'center',
                          }}>{row.size}</button>

                          <div style={{ fontSize: 11, color: 'var(--text-muted)', minWidth: 55 }}>Stock: {row.current_stock}</div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ fontSize: 10.5, color: 'var(--text-muted)', width: 25 }}>Qty</span>
                            <input type="number" min={0} value={row.qty || ''} onChange={e => !alreadyInList && updateBulkRow(i, 'qty', Number(e.target.value))}
                              placeholder="0" disabled={alreadyInList}
                              style={{ width: 65, padding: '6px 8px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 13, outline: 'none', textAlign: 'center' }} />
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ fontSize: 10.5, color: 'var(--text-muted)', width: 15 }}>₹</span>
                            <input type="number" min={0} step={0.01} value={row.rate || ''} onChange={e => !alreadyInList && updateBulkRow(i, 'rate', Number(e.target.value))}
                              placeholder="0" disabled={alreadyInList}
                              style={{ width: 80, padding: '6px 8px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--accent-amber)', fontSize: 13, outline: 'none', textAlign: 'right' }} />
                          </div>

                          <div style={{ flex: 1, textAlign: 'right', fontSize: 12, color: row.enabled && row.qty > 0 ? 'var(--accent-green)' : 'var(--text-muted)', fontWeight: 600 }}>
                            {row.qty > 0 && row.rate > 0 ? `₹${(row.qty * row.rate).toLocaleString('en-IN')}` : '—'}
                          </div>

                          {alreadyInList && <span style={{ fontSize: 9, color: 'var(--accent-green)', fontWeight: 600 }}>Added</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Bulk totals + confirm */}
              {bulkActive.length > 0 && (
                <div style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', padding: '14px 16px', marginBottom: 20, display: 'flex', justifyContent: 'space-between' }}>
                  <div><div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginBottom: 4 }}>Total Qty</div><div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)' }}>{bulkTotalQty}</div></div>
                  <div style={{ textAlign: 'right' }}><div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginBottom: 4 }}>Total Amount</div><div style={{ fontSize: 18, fontWeight: 800, color: 'var(--accent-amber)' }}>₹{bulkTotalAmount.toLocaleString('en-IN')}</div></div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={handleBulkConfirm} disabled={bulkActive.length === 0} style={{
                  flex: 1, padding: 12,
                  background: bulkActive.length > 0 ? 'linear-gradient(135deg, var(--accent-blue), var(--accent-purple))' : 'var(--bg-elevated)',
                  border: 'none', borderRadius: 'var(--radius-sm)',
                  color: bulkActive.length > 0 ? '#fff' : 'var(--text-muted)', fontSize: 13, fontWeight: 600,
                  cursor: bulkActive.length > 0 ? 'pointer' : 'default',
                }}>Add {bulkActive.length} Size{bulkActive.length !== 1 ? 's' : ''} to Order</button>
                <button onClick={() => setShowBulkAdd(false)} style={{ padding: '12px 20px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>Cancel</button>
              </div>
              </>}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Proforma;
